/* eslint-disable camelcase */
import { put } from 'redux-saga/effects';
import axios from 'src/api/Api';
import { errorHandler } from 'src/errorHandler/errorHandler';
import { logUiErrorToBackend } from 'src/utils/logUiErrorToBackend';
import {
    GET_SURGERY_DETAILS_SUCCESS,
    GET_SURGERY_DETAILS_FAILURE,
    getDropdownValues,
} from '../actions';
import {
    GET_CONSIGNMENT_ADD_MYCASEREF_SUCCESS,
    SET_CONSIGNMENT_ADD_ORDER_SUBMITTED,
} from '../actions/actionTypes';
import {
    setSurgeryDetails,
    setCartData,
    setShippingDetailsEditMode,
    setProgressBarStateEditMode,
    validateProducts,
} from 'src/pages/consignmentAdd/redux/actions';
import { accountNumberZeroTrimmer, generate_UUID, convertObjectToQueryParams as buildQueryParams, setMyCaseRef } from 'src/utils/util';
import { convertObjectToQueryParams } from 'src/utils/util';
import { calculateDiscountPercentage } from '../../components/common/utils/eventHandlerUtil';
import { getTotalExtendedPrice } from '../../components/common/utils/eventHandlerUtil';
import { CONSIGNMENT_ADD_ORDERTYPE } from '../../components/common/constants';
import i18n from 'src/i18n.js';

export const getCareOfFieldFromSummary = (shipping = {}, transferRecipients = []) =>
    shipping?.careOfField ||
    shipping?.transferRecipient?.careOfField ||
    transferRecipients?.find(
        (recipient) => recipient?.salesOrg === shipping?.salesOrg
    )?.careOfField ||
    transferRecipients?.[0]?.careOfField ||
    '';
export const getSpecialStockPartnerNumber = (value = '') => {
    const stockPartnerValue = value?.toString().trim();
    const partnerNumber = stockPartnerValue.match(/\d+$/)?.[0];
    return partnerNumber || stockPartnerValue;
};

export const getOrderHistorySummaryResult = (orderHistoryData) => {
    const orderBasicDetails = orderHistoryData?.orderBasicDetails || {};
    const orderSummaryDetail = orderBasicDetails?.orderSummaryDetail || {};
    const procedureDetail = orderBasicDetails?.procedureDetail || {};
    const territoryDetails =
        orderSummaryDetail?.derivedTerritoryDetails?.[0] || {};
    const contactParts = (orderSummaryDetail?.contact || '')
        .split('/')
        .map((value) => value.trim());

    const billToName =
        orderBasicDetails?.billToName ||
        orderSummaryDetail?.accountName ||
        orderBasicDetails?.accountName ||
        '';
    const specialStockPartnerDisplay = orderHistoryData?.specialStockPartner || '';
    const specialStockPartnerNumber = getSpecialStockPartnerNumber(
        specialStockPartnerDisplay
    );
    const fallbackOrderDetail =
        orderHistoryData?.orderDetail || orderHistoryData?.orderDetails?.[0] || {};
    const billToNumber =
        orderBasicDetails?.billToNumber ||
        orderBasicDetails?.billToId ||
        orderSummaryDetail?.billToNumber ||
        orderSummaryDetail?.billToId ||
        fallbackOrderDetail?.billToNumber ||
        fallbackOrderDetail?.billToId ||
        orderHistoryData?.billToNumber ||
        '';
    const accountNumber =
        orderBasicDetails?.accountNumber ||
        orderBasicDetails?.accountId ||
        orderSummaryDetail?.accountNumber ||
        orderSummaryDetail?.accountId ||
        fallbackOrderDetail?.accountNumber ||
        fallbackOrderDetail?.accountId ||
        fallbackOrderDetail?.soldToNumber ||
        fallbackOrderDetail?.soldToId ||
        orderHistoryData?.accountNumber ||
        orderHistoryData?.accountId ||
        '';
    const accountName =
        orderBasicDetails?.accountName ||
        orderSummaryDetail?.accountName ||
        fallbackOrderDetail?.accountName ||
        fallbackOrderDetail?.soldToName ||
        orderHistoryData?.accountName ||
        orderHistoryData?.soldToName ||
        '';
    const territoryUID = (orderSummaryDetail?.territoryUID || '')
        .toString()
        .trim();
    const territoryCode = (
        orderSummaryDetail?.territoryCode ||
        territoryDetails?.territory ||
        ''
    )
        .toString()
        .trim();
    const orderDetails = Array.isArray(orderHistoryData?.orderDetails)
        ? orderHistoryData.orderDetails
        : [];
    const shippingDetails =
        orderBasicDetails?.shippingDetails ||
        orderSummaryDetail?.shippingDetails ||
        orderHistoryData?.shippingDetails ||
        [];
    const transferRecipients = orderBasicDetails?.transferRecipients || [];

    return {
        accountDetails: {
            accountId: accountNumber,
            accountName,
            districtId:
                orderSummaryDetail?.districtUID ||
                orderSummaryDetail?.districtUid ||
                orderBasicDetails?.districtUID ||
                orderSummaryDetail?.districtId ||
                orderSummaryDetail?.districtCode ||
                '',
            districtCode: orderSummaryDetail?.districtCode || '',
            districtName: orderSummaryDetail?.districtName || '',
            territoryId: territoryUID || territoryCode,
            territoryCode,
            territoryName:
                orderSummaryDetail?.territoryName ||
                territoryDetails?.territoryName ||
                '',
            patientContactName: contactParts?.[0] || '',
            patientContactNumber: contactParts?.[1] || '',
            physicianName: orderSummaryDetail?.physicianName || '',
            surgeryDateTime: orderBasicDetails?.surgeryDate || '',
            procedureCode: procedureDetail?.procedureCode || '',
            requestedDeliveryDate:
                orderSummaryDetail?.requestedDeliveryDate || '',
            surgeryRequestDeliveryDateTime:
                orderSummaryDetail?.requestedDeliveryDate || '',
            isLongTermConsignment: orderHistoryData?.isLongTermConsignment ?? false,
            isShortTermConsignment: orderHistoryData?.isShortTermConsignment ?? false,
            billToId: billToNumber,
            specialStockPartner: {
                sbName: specialStockPartnerDisplay,
                sbNumber: specialStockPartnerNumber,
                sbFormattedName: specialStockPartnerDisplay,
            },
        },
        cartData: {
            products: orderDetails
                .filter(
                    (item) =>
                        (item?.cfn || '').toString().trim().length > 0 &&
                        (item?.approval || '').toString().toUpperCase() !== 'UNAPPROVED'
                )
                .map((item) => ({
                    productCFN: item?.cfn,
                    cfn: item?.cfn,
                    description: item?.description || '',
                    quantity: item?.quantity ?? 0,
                    salesOrg: item?.salesOrg || '',
                    uom: item?.uom || '',
                    isSet: item?.isSet ?? false,
                    implantable: item?.implantable ?? false,
                    instrument: item?.instrument ?? false,
                    unitPrice: item?.unitPrice,
                    basePrice:
                        item?.unitPrice != null
                            ? String(item.unitPrice)
                            : undefined,
                    extPrice:
                        item?.extPrice != null
                            ? String(item.extPrice)
                            : undefined,
                    overriddenUnitPrice: '',
                })),
            pricingNotes: '',
            overriddenReason: '',
        },
        shippingDetails,
        transferRecipients,
        isFromUsage: false,
        myCaseRef: null,
    };
};

export default function* getSurgeryDetails(action) {
        const { myCaseRef, updateState, resourceType, isFromOrderHistory, isCopyFromSummary } = action.payload;

    const progressBarState = {
        Account: {
            label: 'Account',
            displayLabel: i18n.t('equity.progressBar.account'),
            progress: 0,
            current: true,
            done: false,
        },
        Cart: {
            label: 'Cart',
            displayLabel: i18n.t('equity.progressBar.cart'),
            progress: 50,
            current: false,
            done: false,
        },
        Shipping: {
            label: 'Shipping',
            displayLabel: i18n.t('equity.progressBar.shipping'),
            progress: 0,
            current: false,
            done: false,
        },
        Submit: {
            label: 'Submit',
            displayLabel: i18n.t('equity.progressBar.submit'),
            progress: 0,
            current: false,
            done: false,
        },
    };
    const objParams = {
        myCaseRef: myCaseRef,
        resourceType: resourceType,
    };

    const isFalsyValueRequired = false;
    let url = `/occ/v2/${localStorage.getItem(
        'baseSiteId'
    )}/consignment/consignmentAdd?${convertObjectToQueryParams(
        objParams,
        isFalsyValueRequired
    )}`;

    try {
        let result;
        let generatedMyCaseRef = '';
        if (isFromOrderHistory) {
            const histParams = buildQueryParams({ myCaseRef, resourceType: 'ALL' });
            const summaryEndpoint = isCopyFromSummary
                ? 'summary'
                : 'orderHistorySummary';
            const histUrl = `/occ/v2/${localStorage.getItem('baseSiteId')}/orders/${summaryEndpoint}?${histParams}`;
            const histData = yield axios.get(histUrl).then((res) => res.data);
            result = getOrderHistorySummaryResult(histData);
            const generateMyCaseRefUrl = `/occ/v2/${localStorage.getItem('baseSiteId')}/order/generateMyCaseRef`;
            const generatedMyCaseRefResult = yield axios.get(generateMyCaseRefUrl).then((res) => res.data);
            generatedMyCaseRef = generatedMyCaseRefResult?.myCaseRef || '';
            setMyCaseRef(generatedMyCaseRef);
            yield put({
                type: GET_CONSIGNMENT_ADD_MYCASEREF_SUCCESS,
                myCaseRef: generatedMyCaseRef,
                statusCode: generatedMyCaseRefResult?.statusCode,
                statusMessage: generatedMyCaseRefResult?.statusMessage,
            });
            result.myCaseRef = generatedMyCaseRef;
        } else {
            result = yield axios.get(url).then((response) => response.data);
        }
        const {
            accountDetails,
            cartData,
            shippingDetails,
            transferRecipients,
            isFromUsage,
        } = result;
        if (accountDetails) {
            accountDetails.myCaseRef = generatedMyCaseRef || accountDetails.myCaseRef;
            const trimmedAccountName = (accountDetails.accountName || '').trim();
            const lastChar =
                trimmedAccountName[trimmedAccountName.length - 1] || '';
            const cleanAccountName = ['.', '!', '?'].includes(lastChar)
                ? trimmedAccountName.slice(0, -1)
                : trimmedAccountName;
            const accountIdData = {
                accountId: accountDetails.accountId,
                accountName: cleanAccountName,
                accountFormattedName:
                    cleanAccountName +
                    accountNumberZeroTrimmer(accountDetails.accountId),
            };
            //delete surgeryDetails.accountName;
            // delete accountDetails.accountName;
            delete accountDetails.districtCode;
            delete accountDetails.districtName;
            delete accountDetails.lastModifiedBy;
            delete accountDetails.lastModifiedDate;
            delete accountDetails.territoryName;
            delete accountDetails.territoryCode;
            // getAccountsParams = {
            //     districtId: accountDetails.districtId,
            //     territoryId: accountDetails.territoryId,
            // };
            // accountsUrl += `/aardt/accounts?${convertObjectToQueryParams(
            //     getAccountsParams,
            //     isFalsyValueRequired
            // )}`;
            // let accountResult = yield axios
            //     .get(accountsUrl)
            //     .then((response) => response.data);
            yield put(
                getDropdownValues({
                    dropDownName: 'territories',
                    value: accountDetails.districtId,
                })
            );
            yield put(
                getDropdownValues({
                    dropDownName: 'accounts',
                    value: {
                        districtId: accountDetails.districtId,
                        territoryId: accountDetails.territoryId,
                    },
                    districtId: accountDetails.districtId,
                    territoryId: accountDetails.territoryId,
                })
            );
            if (accountDetails.accountId) {
                yield put(
                    getDropdownValues({
                        dropDownName: 'specialStockPartnerDetails',
                        value: {
                            district: accountDetails.districtId
                                ? accountDetails.districtId
                                : 'ALL_Districts',
                            territory: accountDetails.territoryId
                                ? accountDetails.territoryId
                                : 'All_Territories',
                            sapPartnerId: accountDetails.accountId,
                        },
                    })
                );
                yield put(
                    getDropdownValues({
                        dropDownName: 'billToDetails',
                        value: {
                            district: accountDetails.districtId
                                ? accountDetails.districtId
                                : 'ALL_Districts',
                            territory: accountDetails.territoryId
                                ? accountDetails.territoryId
                                : 'All_Territories',
                            sapPartnerId: accountDetails.accountId,
                        },
                    })
                );
            }
            accountDetails['accountId'] = accountIdData;
        }

        const pricingNotes = cartData && cartData?.pricingNotes;
        const overriddenReason = cartData && cartData?.overriddenReason;
        var cartProducts =
            cartData.products &&
            cartData.products.map((product) => ({
                ...product,
                productCFN: product.productCFN,
                quantity: +product.quantity,
                batchCurrentLocation: product?.batchAllocation?.currentLocation,
                batchId: product?.batchAllocation?.id,
                batchParked: product?.batchAllocation?.isParked,
                productKey: generate_UUID(),
                discount: product?.overriddenUnitPrice
                    ? calculateDiscountPercentage(
                          parseFloat(product?.unitPrice),
                          product?.overriddenUnitPrice &&
                              parseFloat(product?.overriddenUnitPrice)
                              ? parseFloat(product?.overriddenUnitPrice)
                              : 0
                      )
                    : product?.overriddenUnitPrice,
                adjustedPrice: product?.overriddenUnitPrice,
                adjustedUnitPrice: product?.overriddenUnitPrice,
                adjustedExtPrice: product?.overriddenUnitPrice
                    ? getTotalExtendedPrice(
                          product?.overriddenUnitPrice,
                          product?.quantity,
                          CONSIGNMENT_ADD_ORDERTYPE
                      )
                    : product?.overriddenUnitPrice,
            }));
        // yield put(disableSurgeryDate(false));
        const bundleChildItems = cartProducts?.filter(
            (item) => item.parentCFN?.length > 0
        );
        cartProducts = cartProducts
            ?.filter((x) => !x.parentCFN)
            ?.map((product) => {
                const productObj = { ...product };
                if (
                    (product.productCFN.toUpperCase().startsWith('M2') ||
                        product.productCFN.toUpperCase().startsWith('BUN') ||
                        product.productCFN.toUpperCase().startsWith('KEN') ||
                        product.productCFN.toUpperCase().startsWith('M1')) &&
                    bundleChildItems?.length > 0
                ) {
                    productObj.children = bundleChildItems?.filter(
                        (child) => child.parentCFN === product.productCFN
                    );
                    productObj.materialType = 'ZKMT';
                }
                return productObj;
            });

        const shippingObject = {
            S030: {
                shippingMethodId: '',
                shippingAddressId: '',
                attention: '',
                salesOrg: '',
                userAcknowledgement: false,
                payment: {
                    poAttachment: '',
                    poNumber: '',
                },
            },
            S002: {
                shippingMethodId: '',
                shippingAddressId: '',
                attention: '',
                salesOrg: '',
                userAcknowledgement: false,
                payment: {
                    poAttachment: '',
                    poNumber: '',
                },
            },
        };
        if (shippingDetails) {
            shippingDetails?.forEach((shipping) => {
                const salesOrg =
                    shipping?.salesOrg === 'S030' || shipping?.salesOrg === 'S002'
                        ? shipping?.salesOrg
                        : 'S002';

                if (isFromOrderHistory) {
                    const transferRecipientFromSummary =
                        shipping?.transferRecipient?.shipToAccount ||
                        shipping?.transferRecipient?.addressId ||
                        shipping?.transferRecipient?.shippingAddressId ||
                        shipping?.transferRecipient ||
                        transferRecipients?.[0]?.shipToAccount ||
                        transferRecipients?.[0]?.addressId ||
                        '';
                    const careOfFieldFromSummary = getCareOfFieldFromSummary(
                        shipping,
                        transferRecipients
                    );
                    shippingObject[salesOrg] = {
                        shippingMethodId:
                            shipping?.shipMethod ||
                            shipping?.methodName ||
                            shipping?.method?.id ||
                            shipping?.shippingMethodId ||
                            '',
                        shippingAddressId:
                            shipping?.shippingAddressId ||
                            shipping?.shipToAccount ||
                            shipping?.shipToAccountNumber ||
                            '',
                        transferRecipient: transferRecipientFromSummary,
                        careOfField: careOfFieldFromSummary,
                        attention: shipping?.attention || '',
                        salesOrg,
                        userAcknowledgement: !!shipping?.userAcknowledgement,
                        payment: {
                            poAttachment: shipping?.payment?.poAttachment || '',
                            poNumber: '',
                        },
                    };
                } else if (shipping?.method?.id && shipping?.shippingAddressId) {
                    shippingObject[salesOrg] = {
                        shippingMethodId: shipping?.method?.id,
                        shippingAddressId: shipping?.shippingAddressId,
                        attention: shipping?.attention,
                        salesOrg: shipping?.salesOrg,
                        userAcknowledgement: shipping?.userAcknowledgement,
                        payment: {
                            poAttachment: shipping?.payment?.poAttachment,
                            poNumber: shipping?.payment?.poNumber,
                        },
                    };
                }
            });
        }

        if (cartData?.products?.length > 0 && shippingDetails) {
            progressBarState['Cart'].progress = 100;
            progressBarState['Cart'].done = true;
            progressBarState['Shipping'].progress = 100;
            progressBarState['Shipping'].done = true;
            progressBarState['Shipping'].current = false;
            progressBarState['Submit'].progress = 100;
            progressBarState['Account'].current = true;
            progressBarState['Account'].done = true;
        } else if (accountDetails && cartData?.products?.length > 0) {
            progressBarState['Cart'].progress = 100;
            progressBarState['Cart'].done = true;
            progressBarState['Cart'].current = false;
            progressBarState['Shipping'].progress = 100;
            progressBarState['Shipping'].current = false;
            progressBarState['Shipping'].done = false;
            progressBarState['Submit'].progress = 50;
            progressBarState['Account'].current = true;
            progressBarState['Account'].done = true;
        } else {
            progressBarState['Cart'].progress = 100;
            progressBarState['Cart'].done = false;
            progressBarState['Cart'].current = false;
            progressBarState['Shipping'].progress = 50;
            progressBarState['Shipping'].current = false;
            progressBarState['Shipping'].done = false;
            progressBarState['Submit'].progress = 0;
            progressBarState['Account'].current = true;
            progressBarState['Account'].done = true;
        }
        if (isFromOrderHistory || isCopyFromSummary) {
            progressBarState['Account'].done = false;
            yield put({
                type: SET_CONSIGNMENT_ADD_ORDER_SUBMITTED,
                data: false,
            });
        }
        if (updateState) {
            yield put(setSurgeryDetails(accountDetails));
            yield put(
                setCartData({
                    products: cartProducts,
                    pricingNotes: pricingNotes,
                    overriddenReason: overriddenReason,
                    getPricesForItems: cartProducts,
                    isFromUsage: isFromUsage,
                    // On copy, myCaseRef is the SOURCE order; sending it as parentMyCaseRef fails derivedterritories (10816).
                    reportUsageMyCaseRef:
                        isFromOrderHistory || isCopyFromSummary
                            ? null
                            : myCaseRef,
                })
            );
            yield put(setShippingDetailsEditMode(shippingObject));
            // yield put(handleSurgeryNotes(notes || []));
            // yield put(
            //     changeAcknowlegmentCheckbox({
            //         name: 'pricingNotes',
            //         value: pricingNotes ? pricingNotes : '',
            //     })
            // );
            yield put(setProgressBarStateEditMode(progressBarState));
            if (isFromOrderHistory || isCopyFromSummary) {
                const productsForValidation = (cartProducts || []).filter(
                    (product) => product.quantity > 0
                );
                if (productsForValidation.length > 0) {
                    yield put(
                        validateProducts({
                            products: productsForValidation.map((product) => ({
                                ...product,
                                cfn: product.cfn || product.productCFN,
                            })),
                            orderType: CONSIGNMENT_ADD_ORDERTYPE,
                            validateOnly: true,
                        })
                    );
                }
            }
        }
        yield put({ type: GET_SURGERY_DETAILS_SUCCESS, payload: result });
    } catch (e) {
        logUiErrorToBackend(e, {
            apiEndPoint: url,
            requestPayload: action.payload
        });
        yield put({
            type: GET_SURGERY_DETAILS_FAILURE,
            payload: errorHandler('503'),
        });
    }
}
